import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { cuentasPorCobrarAPI, ventasAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import ModalEditarItemsVenta from '../components/ModalEditarItemsVenta';

const CuentasPorCobrar = () => {
  const { formatCurrency } = useApp();
  const { esAdminOGerente, modoEspectador } = useAuth();

  // Datos
  const [cuentas, setCuentas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // Filtros
  const [buscar, setBuscar] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Detalle + Abono
  const [showDetalle, setShowDetalle] = useState(null);
  const [abonosHistorial, setAbonosHistorial] = useState([]);
  const [loadingAbonos, setLoadingAbonos] = useState(false);
  const [showAbonoModal, setShowAbonoModal] = useState(false);
  const [abonoForm, setAbonoForm] = useState({
    monto: '', metodo_pago: 'efectivo', referencia: '', notas: '', numero_orden: '',
    motivo: '', item_especifico_id: '',
    codigo_autorizacion: '', ultimos_digitos: '', marca_tarjeta: 'Visa', tipo_tarjeta: 'credito',
    facturar_sri: null
  });
  const [procesandoAbono, setProcesandoAbono] = useState(false);
  // Editar ítems (admin/gerente) + reembolsos que dejó la edición
  const [showEditarItems, setShowEditarItems] = useState(false);
  const [reembolsosHistorial, setReembolsosHistorial] = useState([]);

  const cargarDatos = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (buscar) params.buscar = buscar;
      if (estadoFiltro) params.estado = estadoFiltro;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;

      const [cuentasRes, resumenRes] = await Promise.all([
        cuentasPorCobrarAPI.getAll(params),
        cuentasPorCobrarAPI.getResumen()
      ]);

      if (cuentasRes.data.success) {
        setCuentas(cuentasRes.data.data);
        setPagination(cuentasRes.data.pagination);
      }
      if (resumenRes.data.success) setResumen(resumenRes.data.data);
    } catch { toast.error('Error al cargar cuentas por cobrar'); }
    setLoading(false);
  }, [buscar, estadoFiltro, fechaDesde, fechaHasta]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const cargarAbonos = async (ventaId) => {
    setLoadingAbonos(true);
    try {
      const res = await cuentasPorCobrarAPI.getAbonos(ventaId);
      if (res.data.success) {
        setAbonosHistorial(res.data.data);
        setReembolsosHistorial(res.data.reembolsos || []);
      }
    } catch { setAbonosHistorial([]); setReembolsosHistorial([]); }
    setLoadingAbonos(false);
  };

  const verDetalle = async (cuenta) => {
    setShowDetalle(cuenta);
    setAbonosHistorial([]);
    setReembolsosHistorial([]);
    cargarAbonos(cuenta.id);
  };

  const abrirAbono = () => {
    setAbonoForm({
      // numero_orden va aquí también: sin él el reset deja el input sin valor (undefined) y
      // React lo pasa de controlado a no controlado en la segunda apertura del modal.
      monto: '', metodo_pago: 'efectivo', referencia: '', notas: '', numero_orden: '',
      motivo: '', item_especifico_id: '',
      codigo_autorizacion: '', ultimos_digitos: '', marca_tarjeta: 'Visa', tipo_tarjeta: 'credito',
      // [Elección SRI al saldar] null = sin elegir. Obligatorio cuando el abono cierra la cuenta.
      facturar_sri: null
    });
    setShowAbonoModal(true);
  };

  // ─── [Elección SRI al saldar] ───────────────────────────────────────────
  // Saldar la cuenta dispara la factura electrónica, que es irreversible (solo se
  // deshace con nota de crédito). Antes salía sola en background. Ahora, si este
  // abono deja el saldo en 0 y la venta aún no está facturada, hay que elegir.
  // No aplica a Consumidor Final (nunca factura al SRI) ni a ventas ya facturadas.
  const abonoSaldaCuenta = () => {
    const saldo = parseFloat(showDetalle?.saldo_pendiente) || 0;
    // Redondeado a centavos igual que el backend (`montoAbono`), si no un monto con 3
    // decimales haría que frontend y backend discrepen sobre si la cuenta queda saldada.
    const monto = parseFloat((parseFloat(abonoForm.monto) || 0).toFixed(2));
    return monto > 0 && parseFloat((saldo - monto).toFixed(2)) <= 0;
  };
  const requiereEleccionSri = () => (
    abonoSaldaCuenta() && !showDetalle?.facturada &&
    !!showDetalle?.cliente_id && Number(showDetalle.cliente_id) !== 1
  );

  const registrarAbono = async () => {
    if (!showDetalle) return;
    const monto = parseFloat(abonoForm.monto);
    if (!monto || monto <= 0) { toast.error('Ingrese un monto válido'); return; }
    if (monto > parseFloat(showDetalle.saldo_pendiente)) { toast.error('El abono excede el saldo pendiente'); return; }
    // [Cuentas por Cobrar] Motivo/justificativo OPCIONAL: validacion de obligatoriedad removida.
    // [Elección SRI al saldar] El backend también lo exige (HTTP 400); esto evita el viaje.
    if (requiereEleccionSri() && abonoForm.facturar_sri === null) {
      toast.error('Este abono salda la cuenta: elija si se factura CON SRI o SIN SRI.');
      return;
    }

    setProcesandoAbono(true);
    try {
      const payload = {
        monto: abonoForm.monto,
        metodo_pago: abonoForm.metodo_pago,
        referencia: abonoForm.referencia || null,
        notas: abonoForm.notas || null,
        motivo: (abonoForm.motivo || '').trim(),
        item_especifico_id: abonoForm.item_especifico_id || null,
        numero_orden: abonoForm.numero_orden || null
      };
      // [Elección SRI al saldar] Solo se envía cuando hubo decisión explícita.
      if (requiereEleccionSri()) payload.facturar_sri = abonoForm.facturar_sri;
      if (abonoForm.metodo_pago === 'tarjeta_medianet') {
        payload.codigo_autorizacion = abonoForm.codigo_autorizacion;
        payload.ultimos_digitos = abonoForm.ultimos_digitos;
        payload.marca_tarjeta = abonoForm.marca_tarjeta;
        payload.tipo_tarjeta = abonoForm.tipo_tarjeta;
      }

      const res = await ventasAPI.registrarAbono(showDetalle.id, payload);
      if (res.data.success) {
        toast.success(`Abono de $${(parseFloat(monto) || 0).toFixed(2)} registrado`); // FIX: null safety
        // [Elección SRI al saldar] Confirmar al cajero qué pasó con el comprobante.
        if (res.data.data?.cuenta_saldada) {
          if (res.data.data.facturar_sri === true) {
            if (res.data.data.factura_en_proceso) toast.info('Cuenta saldada — se está generando la factura SRI.');
            else toast.warning('Cuenta saldada, pero la facturación automática está APAGADA: la factura NO se emitió. Génerela manualmente.');
          } else if (res.data.data.facturar_sri === false) {
            toast.info('Cuenta saldada SIN factura SRI — entregue el comprobante interno.');
          }
        }
        setShowAbonoModal(false);
        // Actualizar datos locales
        setShowDetalle(prev => ({
          ...prev,
          monto_abonado: res.data.data.monto_abonado,
          saldo_pendiente: res.data.data.saldo_pendiente,
          estado_cuenta: res.data.data.saldo_pendiente <= 0 ? 'pagada' : prev.estado_cuenta
        }));
        cargarAbonos(showDetalle.id);
        cargarDatos(pagination.page);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al registrar abono');
    }
    setProcesandoAbono(false);
  };

  // Tras editar ítems: reflejar los nuevos números sin cerrar el detalle.
  const onItemsEditados = (d) => {
    setShowEditarItems(false);
    setShowDetalle(prev => ({
      ...prev,
      total: d.despues.total,
      monto_abonado: d.despues.monto_abonado,
      saldo_pendiente: d.despues.saldo_pendiente,
      estado_cuenta: d.despues.saldo_pendiente <= 0 ? 'pagada' : prev.estado_cuenta,
      facturar_sri: d.facturar_sri !== null && d.facturar_sri !== undefined ? d.facturar_sri : prev.facturar_sri,
      items: d.detalle && d.detalle.length > 0 ? d.detalle : prev.items
    }));
    cargarAbonos(d.venta_id);
    cargarDatos(pagination.page);
  };
  const cerrarEditarItems = useCallback(() => setShowEditarItems(false), []);

  const metodoLabel = (m) => {
    const map = { efectivo: 'Efectivo', tarjeta_medianet: 'Tarjeta', tarjeta_payphone: 'Payphone', transferencia: 'Transferencia', credito: 'Crédito', mixto: 'Mixto' };
    return map[m] || m;
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0"><i className="bi bi-wallet2 me-2 text-warning"></i>Cuentas por Cobrar</h4>
      </div>

      {/* Resumen Cards */}
      {resumen && (
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #ffc107' }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <small className="text-muted">Total Pendiente</small>
                    <h4 className="mb-0 text-warning">{formatCurrency(resumen.total_pendiente)}</h4>
                  </div>
                  <div className="align-self-center"><i className="bi bi-wallet2 fs-2 text-warning opacity-50"></i></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #dc3545' }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <small className="text-muted">Total Vencido</small>
                    <h4 className="mb-0 text-danger">{formatCurrency(resumen.total_vencido)}</h4>
                  </div>
                  <div className="align-self-center"><i className="bi bi-exclamation-triangle fs-2 text-danger opacity-50"></i></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #0d6efd' }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <small className="text-muted">Cuentas Activas</small>
                    <h4 className="mb-0 text-primary">{resumen.cantidad_cuentas}</h4>
                  </div>
                  <div className="align-self-center"><i className="bi bi-people fs-2 text-primary opacity-50"></i></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #198754' }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <small className="text-muted">Total Cobrado</small>
                    <h4 className="mb-0 text-success">{formatCurrency(resumen.total_cobrado)}</h4>
                  </div>
                  <div className="align-self-center"><i className="bi bi-check-circle fs-2 text-success opacity-50"></i></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por cliente, CI, código, N° factura u orden..."
                  value={buscar}
                  onChange={e => setBuscar(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
                <option value="">Todas</option>
                <option value="pendiente">Pendientes</option>
                <option value="vencida">Vencidas</option>
                <option value="pagada">Pagadas</option>
              </select>
            </div>
            <div className="col-md-2">
              <input type="date" className="form-control form-control-sm" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            </div>
            <div className="col-md-2">
              <input type="date" className="form-control form-control-sm" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
            </div>
            <div className="col-md-2 text-end">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setBuscar(''); setEstadoFiltro(''); setFechaDesde(''); setFechaHasta(''); }}>
                <i className="bi bi-x-circle me-1"></i>Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Cliente</th>
                <th>Venta / Factura / Orden</th>
                <th className="text-end">Total</th>
                <th className="text-end">Abonado</th>
                <th className="text-end">Saldo</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-4"><div className="spinner-border text-primary"></div></td></tr>
              ) : cuentas.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-4 text-muted">No hay cuentas por cobrar</td></tr>
              ) : cuentas.map(c => (
                <tr key={c.id} className={c.estado_cuenta === 'vencida' ? 'table-danger' : c.estado_cuenta === 'pagada' ? 'table-success' : ''}>
                  <td>
                    <div className="fw-bold">{c.cliente_nombre}</div>
                    <small className="text-muted">{c.cliente_identificacion}</small>
                  </td>
                  <td>
                    <span className="badge bg-dark fw-bold">{c.codigo}</span>
                    {c.numero_orden && <small className="text-primary fw-bold d-block">Orden: {c.numero_orden}</small>}
                    {c.numero_factura && <small className="text-muted d-block">Fact: <span className="fw-bold text-dark">{c.numero_factura}</span></small>}
                    {c.ordenes_codigos && <small className="text-primary fw-bold d-block">{c.ordenes_codigos}</small>}
                  </td>
                  <td className="text-end fw-bold">{formatCurrency(c.total)}</td>
                  <td className="text-end text-success">{formatCurrency(c.monto_abonado)}</td>
                  <td className="text-end fw-bold text-danger">{formatCurrency(c.saldo_pendiente)}</td>
                  <td>
                    {c.fecha_vencimiento_credito
                      ? new Date(c.fecha_vencimiento_credito).toLocaleDateString('es-EC')
                      : <span className="text-muted">Sin fecha</span>}
                    {c.dias_vencido > 0 && <small className="text-danger d-block">{c.dias_vencido} días</small>}
                  </td>
                  <td>
                    <span className={`badge ${c.estado_cuenta === 'pagada' ? 'bg-success' : c.estado_cuenta === 'vencida' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                      {c.estado_cuenta === 'pagada' ? 'Pagada' : c.estado_cuenta === 'vencida' ? 'Vencida' : 'Pendiente'}
                    </span>
                    {c.total_abonos > 0 && <small className="d-block text-muted">{c.total_abonos} abono{c.total_abonos > 1 ? 's' : ''}</small>}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-outline-primary btn-sm" onClick={() => verDetalle(c)} title="Ver detalle">
                        <i className="bi bi-eye"></i>
                      </button>
                      {parseFloat(c.saldo_pendiente) > 0 && (
                        <button className="btn btn-warning btn-sm" onClick={() => { verDetalle(c); setTimeout(() => abrirAbono(), 300); }} title="Registrar abono">
                          <i className="bi bi-cash-coin"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2">
            <small className="text-muted">Mostrando {cuentas.length} de {pagination.total}</small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => cargarDatos(pagination.page - 1)}>Anterior</button>
                </li>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, pagination.page - 2) + i;
                  if (pageNum > pagination.totalPages) return null;
                  return (
                    <li key={pageNum} className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => cargarDatos(pageNum)}>{pageNum}</button>
                    </li>
                  );
                })}
                <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => cargarDatos(pagination.page + 1)}>Siguiente</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Modal Detalle */}
      {showDetalle && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-wallet2 me-2"></i>Cuenta - <span className="fw-bold">{showDetalle.codigo}</span></h5>
                <button className="btn-close" onClick={() => setShowDetalle(null)}></button>
              </div>
              <div className="modal-body">
                {/* Info cliente */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <small className="text-muted">Cliente</small>
                    <p className="fw-bold mb-1">{showDetalle.cliente_nombre}</p>
                    <small className="text-muted">{showDetalle.cliente_identificacion}</small>
                    {showDetalle.cliente_celular && <small className="d-block text-muted"><i className="bi bi-phone me-1"></i>{showDetalle.cliente_celular}</small>}
                  </div>
                  <div className="col-md-6 text-end">
                    <small className="text-muted">Vendedor</small>
                    <p className="fw-bold mb-1">{showDetalle.vendedor}</p>
                    <small className="text-muted">{showDetalle.created_at ? new Date(showDetalle.created_at).toLocaleString('es-EC') : '—'}</small>
                  </div>
                </div>

                {/* Barra de progreso */}
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#fff8e1' }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total: <strong>{formatCurrency(showDetalle.total)}</strong></span>
                    <span>
                      <span className={`badge ${showDetalle.estado_cuenta === 'pagada' ? 'bg-success' : showDetalle.estado_cuenta === 'vencida' ? 'bg-danger' : 'bg-warning text-dark'} me-2`}>
                        {showDetalle.estado_cuenta === 'pagada' ? 'PAGADA' : showDetalle.estado_cuenta === 'vencida' ? 'VENCIDA' : 'PENDIENTE'}
                      </span>
                      {showDetalle.fecha_vencimiento_credito && (
                        <small>Vence: {new Date(showDetalle.fecha_vencimiento_credito).toLocaleDateString('es-EC')}</small>
                      )}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span>Abonado: <strong className="text-success">{formatCurrency(showDetalle.monto_abonado)}</strong></span>
                    <span>Saldo: <strong className="text-danger">{formatCurrency(showDetalle.saldo_pendiente)}</strong></span>
                  </div>
                  <div className="progress" style={{ height: '14px' }}>
                    <div
                      className="progress-bar bg-success"
                      style={{ width: `${Math.min(100, ((parseFloat(showDetalle.monto_abonado) || 0) / (parseFloat(showDetalle.total) || 1)) * 100)}%` }}
                    >
                      {Math.round(((parseFloat(showDetalle.monto_abonado) || 0) / (parseFloat(showDetalle.total) || 1)) * 100)}%
                    </div>
                  </div>
                  {parseFloat(showDetalle.saldo_pendiente) > 0 && showDetalle.estado !== 'anulada' && (
                    <button className="btn btn-warning btn-sm mt-2" onClick={abrirAbono}>
                      <i className="bi bi-cash-coin me-1"></i>Registrar Abono
                    </button>
                  )}
                  {/* Editar ítems: solo cuentas abiertas sin factura, solo admin/gerente (el backend lo vuelve a exigir) */}
                  {esAdminOGerente() && !modoEspectador && parseFloat(showDetalle.saldo_pendiente) > 0 && showDetalle.estado !== 'anulada' && !showDetalle.facturada && (
                    <button className="btn btn-outline-primary btn-sm mt-2 ms-2" onClick={() => setShowEditarItems(true)}>
                      <i className="bi bi-pencil-square me-1"></i>Editar ítems
                    </button>
                  )}
                </div>

                {/* Productos / Servicios de la venta */}
                {showDetalle.items && showDetalle.items.length > 0 && (() => {
                  // Threshold: items con total <= $0.05 son token-cents (estuche, paño, cheq visual)
                  // y se consideran "incluidos" (regalos/sin costo real).
                  const esIncluido = (it) => parseFloat(it.total) <= 0.05;
                  const conCosto = showDetalle.items.filter(it => !esIncluido(it));
                  const incluidos = showDetalle.items.filter(esIncluido);
                  return (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <h6 className="mb-0">
                          <i className="bi bi-bag me-2"></i>Productos de la venta
                        </h6>
                        <small className="text-muted">
                          {conCosto.length} con costo
                          {incluidos.length > 0 && ` · ${incluidos.length} incluido${incluidos.length !== 1 ? 's' : ''}`}
                        </small>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Producto</th>
                              <th className="text-end">Cant.</th>
                              <th className="text-end">P. Unit.</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {showDetalle.items.map((it, idx) => {
                              const incluido = esIncluido(it);
                              return (
                                <tr
                                  key={it.id || `${it.producto_codigo}-${idx}`}
                                  className={incluido ? 'text-muted' : ''}
                                  style={incluido ? { backgroundColor: '#fafafa' } : {}}
                                >
                                  <td>
                                    <small className="text-muted d-block">{it.producto_codigo}</small>
                                    <span className={incluido ? 'fst-italic' : ''}>{it.producto_nombre}</span>
                                    {incluido && (
                                      <span
                                        className="badge bg-secondary-subtle text-secondary border ms-2"
                                        style={{ fontSize: '.65rem', fontWeight: 500 }}
                                        title="Producto sin costo (regalo o incluido en la venta)"
                                      >
                                        <i className="bi bi-gift me-1"></i>Incluido
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-end">{it.cantidad}</td>
                                  <td className="text-end">{formatCurrency(it.precio_unitario)}</td>
                                  <td className={`text-end ${incluido ? '' : 'fw-bold'}`}>
                                    {formatCurrency(it.total)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Historial de abonos */}
                <h6 className="mb-2"><i className="bi bi-clock-history me-2"></i>Historial de Abonos</h6>
                {loadingAbonos ? (
                  <div className="text-center py-3"><div className="spinner-border spinner-border-sm"></div></div>
                ) : abonosHistorial.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    <i className="bi bi-inbox fs-3 d-block mb-1"></i>
                    No hay abonos registrados
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr><th>Fecha</th><th>Monto</th><th>Método</th><th>Motivo</th><th>Ítem</th><th>Ref.</th><th>Usuario</th></tr>
                      </thead>
                      <tbody>
                        {abonosHistorial.map(a => (
                          <tr key={a.id}>
                            <td>{a.created_at ? new Date(a.created_at).toLocaleString('es-EC') : '—'}</td>
                            <td className="fw-bold text-success">{formatCurrency(a.monto)}</td>
                            <td>
                              <span className={`badge ${a.metodo_pago === 'efectivo' ? 'bg-success' : a.metodo_pago === 'tarjeta_medianet' ? 'bg-info' : a.metodo_pago === 'transferencia' ? 'bg-primary' : 'bg-secondary'}`}>
                                {metodoLabel(a.metodo_pago)}
                              </span>
                              {a.ultimos_digitos && <small className="ms-1">****{a.ultimos_digitos}</small>}
                            </td>
                            <td className="small" style={{ maxWidth: '200px' }} title={a.motivo}>
                              {a.motivo
                                ? (a.motivo.length > 40 ? a.motivo.substring(0, 40) + '…' : a.motivo)
                                : <span className="text-muted">—</span>}
                            </td>
                            <td className="small">
                              {a.item_producto_nombre
                                ? <span className="badge bg-light text-dark border" title={a.item_producto_codigo}>{a.item_producto_nombre}</span>
                                : <span className="text-muted">General</span>}
                            </td>
                            <td>{a.referencia || a.codigo_autorizacion || a.notas || '-'}</td>
                            <td>{a.usuario_nombre}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Reembolsos por edición de ítems */}
                {reembolsosHistorial.length > 0 && (
                  <>
                    <h6 className="mb-2 mt-3"><i className="bi bi-arrow-return-left me-2 text-danger"></i>Reembolsos</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr><th>Fecha</th><th>Monto</th><th>Método</th><th>Detalle</th><th>Ref.</th><th>Usuario</th></tr>
                        </thead>
                        <tbody>
                          {reembolsosHistorial.map(r => (
                            <tr key={r.id}>
                              <td>{r.created_at ? new Date(r.created_at).toLocaleString('es-EC') : '—'}</td>
                              <td className="fw-bold text-danger">-{formatCurrency(r.monto)}</td>
                              <td>{metodoLabel(r.metodo_pago)}</td>
                              <td className="small">{r.detalle}</td>
                              <td>{r.referencia || '-'}</td>
                              <td>{r.usuario_nombre}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDetalle(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Ítems */}
      {showEditarItems && showDetalle && (
        <ModalEditarItemsVenta ventaId={showDetalle.id} clienteNombre={showDetalle.cliente_nombre}
          onClose={cerrarEditarItems} onGuardado={onItemsEditados} />
      )}

      {/* Modal Registrar Abono */}
      {showAbonoModal && showDetalle && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title"><i className="bi bi-cash-coin me-2"></i>Registrar Abono</h5>
                <button className="btn-close" onClick={() => setShowAbonoModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info py-2 mb-3">
                  <div className="d-flex justify-content-between">
                    <span><strong>{showDetalle.cliente_nombre}</strong></span>
                    <span>Saldo: <strong className="text-danger">{formatCurrency(showDetalle.saldo_pendiente)}</strong></span>
                  </div>
                  <small className="text-muted">Venta: <span className="fw-bold text-dark">{showDetalle.codigo}</span></small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Monto del Abono *</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      value={abonoForm.monto}
                      onChange={e => setAbonoForm({ ...abonoForm, monto: e.target.value })}
                      max={parseFloat(showDetalle.saldo_pendiente)}
                      min="0.01" step="0.01"
                      placeholder="0.00" autoFocus
                    />
                    <button className="btn btn-outline-success"
                      onClick={() => setAbonoForm({ ...abonoForm, monto: (parseFloat(showDetalle.saldo_pendiente) || 0).toFixed(2) })}>
                      Pagar todo
                    </button>
                  </div>
                </div>

                {/* [N° orden física] Código de la orden de papel (opcional) */}
                <div className="mb-3">
                  <label className="form-label fw-semibold small"><i className="bi bi-hash me-1"></i>N° Orden física (opcional)</label>
                  <input type="text" className="form-control" maxLength={50} placeholder="Código de la orden de papel"
                    value={abonoForm.numero_orden} onChange={e => setAbonoForm({ ...abonoForm, numero_orden: e.target.value })} />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Método de Pago</label>
                  <div className="d-flex gap-2">
                    {[
                      { value: 'efectivo', label: 'Efectivo', icon: 'bi-cash', color: 'success' },
                      { value: 'tarjeta_medianet', label: 'Tarjeta', icon: 'bi-credit-card', color: 'info' },
                      { value: 'transferencia', label: 'Transfer.', icon: 'bi-bank', color: 'primary' }
                    ].map(m => (
                      <button key={m.value}
                        className={`btn ${abonoForm.metodo_pago === m.value ? `btn-${m.color}` : `btn-outline-${m.color}`} flex-fill`}
                        onClick={() => setAbonoForm({ ...abonoForm, metodo_pago: m.value })}>
                        <i className={`bi ${m.icon} me-1`}></i>{m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {abonoForm.metodo_pago === 'tarjeta_medianet' && (
                  <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f0f8ff' }}>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small">Código Autorización</label>
                        <input type="text" className="form-control form-control-sm"
                          value={abonoForm.codigo_autorizacion}
                          onChange={e => setAbonoForm({ ...abonoForm, codigo_autorizacion: e.target.value })} />
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Últimos 4 dígitos</label>
                        <input type="text" className="form-control form-control-sm" maxLength={4}
                          value={abonoForm.ultimos_digitos}
                          onChange={e => setAbonoForm({ ...abonoForm, ultimos_digitos: e.target.value.replace(/\D/g, '') })} />
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Marca</label>
                        <select className="form-select form-select-sm" value={abonoForm.marca_tarjeta}
                          onChange={e => setAbonoForm({ ...abonoForm, marca_tarjeta: e.target.value })}>
                          <option>Visa</option><option>Mastercard</option><option>Diners</option><option>Discover</option><option>Amex</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Tipo</label>
                        <select className="form-select form-select-sm" value={abonoForm.tipo_tarjeta}
                          onChange={e => setAbonoForm({ ...abonoForm, tipo_tarjeta: e.target.value })}>
                          <option value="credito">Crédito</option><option value="debito">Débito</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {abonoForm.metodo_pago === 'transferencia' && (
                  <div className="mb-3">
                    <label className="form-label small">Nº Referencia</label>
                    <input type="text" className="form-control form-control-sm"
                      value={abonoForm.referencia}
                      onChange={e => setAbonoForm({ ...abonoForm, referencia: e.target.value })}
                      placeholder="Ej: TRF-123456" />
                  </div>
                )}

                {/* [MIG-039] Lista de items de la venta + selector */}
                {showDetalle.items && showDetalle.items.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Ítems de esta venta</label>
                    <div className="border rounded p-2 mb-2 bg-light" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {showDetalle.items.map(it => (
                        <div key={it.id} className="small d-flex justify-content-between border-bottom py-1">
                          <span>{it.producto_nombre} <span className="text-muted">× {it.cantidad}</span></span>
                          <span className="fw-bold">{formatCurrency(it.total)}</span>
                        </div>
                      ))}
                    </div>
                    <label className="form-label small">Aplicar abono a:</label>
                    <select className="form-select form-select-sm"
                      value={abonoForm.item_especifico_id}
                      onChange={e => {
                        const itemId = e.target.value;
                        const updates = { item_especifico_id: itemId };
                        // [UX 2026-05-07] Auto-sugerir motivo si está vacío o sub-15 chars
                        const motivoActual = (abonoForm.motivo || '').trim();
                        if (motivoActual.length < 15) {
                          if (itemId) {
                            const item = showDetalle.items.find(it => String(it.id) === String(itemId));
                            if (item) {
                              updates.motivo = `Abono sobre ${item.producto_nombre} (${formatCurrency(item.total)})`;
                            }
                          } else {
                            updates.motivo = `Abono general sobre venta ${showDetalle.codigo}`;
                          }
                        }
                        setAbonoForm({ ...abonoForm, ...updates });
                      }}>
                      <option value="">Abono general (toda la venta)</option>
                      {showDetalle.items.map(it => (
                        <option key={it.id} value={it.id}>
                          {it.producto_nombre} ({formatCurrency(it.total)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label small">Notas (opcional)</label>
                  <input type="text" className="form-control form-control-sm"
                    value={abonoForm.notas}
                    onChange={e => setAbonoForm({ ...abonoForm, notas: e.target.value })} />
                </div>

                {/* Motivo / Justificativo: OPCIONAL */}
                <div className="mb-2">
                  <label className="form-label small fw-bold">
                    Motivo / Justificativo <span className="text-muted">(opcional)</span>
                  </label>
                  <textarea className="form-control form-control-sm"
                    rows={2}
                    maxLength={500}
                    placeholder="Se completará automáticamente al elegir un ítem arriba. Edítalo si quieres agregar detalle."
                    value={abonoForm.motivo}
                    onChange={e => setAbonoForm({ ...abonoForm, motivo: e.target.value })} />
                  <div className="text-muted small mt-1">
                    <i className="bi bi-lightbulb me-1"></i>
                    Opcional. Puedes seleccionar el ítem arriba para que el motivo se complete solo.
                  </div>
                </div>

                {/* [Elección SRI al saldar] Obligatoria cuando el abono deja el saldo en 0 */}
                {requiereEleccionSri() && (
                  <div className="border border-warning rounded p-3 bg-warning bg-opacity-10">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-exclamation-triangle-fill text-warning me-2 fs-5"></i>
                      <div>
                        <strong className="d-block">Este abono salda la cuenta por completo</strong>
                        <small className="text-muted">Debe elegir cómo se documenta. La factura electrónica no se puede deshacer sin nota de crédito.</small>
                      </div>
                    </div>
                    <label className="form-label fw-semibold small mb-1">
                      <i className="bi bi-receipt me-1"></i>Facturación <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex gap-2">
                      <button type="button" className={`btn flex-fill ${abonoForm.facturar_sri === true ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setAbonoForm({ ...abonoForm, facturar_sri: true })}>
                        <i className="bi bi-file-earmark-check me-1"></i>Facturar CON SRI
                      </button>
                      <button type="button" className={`btn flex-fill ${abonoForm.facturar_sri === false ? 'btn-dark' : 'btn-outline-dark'}`}
                        onClick={() => setAbonoForm({ ...abonoForm, facturar_sri: false })}>
                        <i className="bi bi-printer me-1"></i>SIN SRI (comprobante interno)
                      </button>
                    </div>
                    {abonoForm.facturar_sri === null && (
                      <div className="small text-danger mt-1"><i className="bi bi-exclamation-circle me-1"></i>Debe elegir una opción para continuar.</div>
                    )}
                    {showDetalle?.facturar_sri === false && abonoForm.facturar_sri === null && (
                      <div className="small text-muted mt-1"><i className="bi bi-info-circle me-1"></i>Al crear esta venta se eligió SIN SRI.</div>
                    )}
                    {showDetalle?.facturar_sri === true && abonoForm.facturar_sri === null && (
                      <div className="small text-muted mt-1"><i className="bi bi-info-circle me-1"></i>Al crear esta venta se eligió CON SRI.</div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAbonoModal(false)}>Cancelar</button>
                <button className="btn btn-warning" onClick={registrarAbono}
                  disabled={procesandoAbono || !abonoForm.monto || (requiereEleccionSri() && abonoForm.facturar_sri === null)}>
                  {procesandoAbono ? <><span className="spinner-border spinner-border-sm me-1"></span>Procesando...</> : <><i className="bi bi-check-lg me-1"></i>Registrar Abono</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasPorCobrar;
